/* eslint-disable react-hooks/exhaustive-deps */
//#region Libraries
import { useEffect } from "react";
import { useState } from "react";
//#endregion
//#region Styles
import { Column, Row, SubtitleSection } from "components/styles";
//#endregion
//#region Components
import CustomButton from "components/CustomButton/CustomButton";
import { CustomRadio } from "components/CustomRadio/CustomRadio";
import { CustomCheckbox } from "components/CustomCheckbox/CustomCheckbox";
//#endregion
import PenalizeDialog from "../../../PenalizeDialog/PenalizeDialog";
import DialogAlertAction from "components/DialogAlertAction/DialogAlertAction";
//#region Icons
import DisketIcon from "icons/DisketIcon";
//#endregion
//#region Services
import { 
    requestPenalizeStudent, 
    requestSaveReservationState, 
    requestUpdatePenaltyStudent 
} from "services/resources/library/aboutLibraryResources";
//#endregion
//#region Utils
import { RESERVATION_STATES, RESERVATION_STATE_VALUES, TOAST_MESSAGES } from "../../../utils/constants";
import { RESOURCES_CONSTANTS } from "config/constants";
import useMainContext from "utils/context/useMainContext";
import useShowDialogAlert from "components/DialogAlert/utils/hooks/useDialogAlert";
import { INIT_FORM as INIT_FORM_PENALIZE } from "views/Recursos/ResourcesLibrary/SchedulesWrapper/ReservationData/PenalizeDialog/utils/constants";
//#endregion

const { RESERVATION_TYPES } = RESOURCES_CONSTANTS;

const CONTROLS_STYLES = {
    [true]: {
        wrapper: {
            marginLeft: "0px"
        },
        content: {
            width: "100%",
            justifyContent: "center"
        }
    },
    [false]: {
        wrapper: {
            marginLeft: "10px"
        },
        content: {
            width: "unset",
            justifyContent: "flex-start"
        }
    }
}

const ReservationState = ({
    reservation, penalizeData,
    resourceName, hasReservationState, 
    showUpdateDialog, reloadData, 
    loadingRequest, isGroupRoom,
    setAreThereChanges
}) => {
    const { isManual, finalized } = reservation;
    const responsableStudent = reservation.student?.responsable || null;
    //#region Hooks
    const mainContext = useMainContext();
    const dialogShowSaveInformationConfirmation = useShowDialogAlert();
    const dialogShowCannotSaveInformation = useShowDialogAlert();
    const dialogShowPenalizeDialog = useShowDialogAlert();
    //#endregion
    //#region States
    const [reservationState, setReservationState] = useState(0);
    const [penalizedStudent, setPenalizedStudent] = useState(false);
    const [penalizeForm, setPenalizeForm] = useState({ ...INIT_FORM_PENALIZE });
    //#endregion
    //#region Effects
    useEffect(() => {
        if (!showUpdateDialog) return;
        preloadingInformation();
    }, [showUpdateDialog]);
    useEffect(() => {
        if (reservationState === RESERVATION_STATE_VALUES.ON_USE) 
            setPenalizedStudent(false);
        setAreThereChanges(reservationState !== convertToReservationState());
    }, [reservationState]);
    //#endregion
    //#region Functions
    const applyPenalty = async () => {
        dialogShowPenalizeDialog.set(false);
        const isUpdate = Boolean(responsableStudent.penalty);
        const params = isUpdate 
            ? {
                loadingRequestMessage: "Editando sanción..." ,
                toastMessage: TOAST_MESSAGES.UPDATE_PENALTY_STUDENT,
                request: requestUpdatePenaltyStudent
            } : {
                loadingRequestMessage: "Sancionando alumno...",
                toastMessage: TOAST_MESSAGES.SAVE_RESERVATION_STATE,
                request: requestPenalizeStudent
            };
        loadingRequest.show(params.loadingRequestMessage);
        // Realizando petición de sanción
        const success = await params.request({
            reservationDetailIds: [responsableStudent.reservationDetailId], 
            studentPenalizedIds: [penalizeForm.studentPenalizedId],
            resourceTypeId: reservation.resourceTypeId,
            ...penalizeForm
        });
        loadingRequest.hide();
        // Gestionando errores
        if (!success) {
            mainContext.setShowDialogUnexpectedError(true);
            return;
        }
        reloadData(params.toastMessage);
    }
    const convertToReservationState = () => {
        if (finalized === null)
            return 0;
        return finalized 
            ? RESERVATION_STATE_VALUES.FINALIZED 
            : RESERVATION_STATE_VALUES.ON_USE;
    }
    const preloadingInformation = () => {
        if (!isManual) 
            setPenalizedStudent(responsableStudent.penalty !== null);
        if (!hasReservationState) {
            setReservationState(0);
            return;
        }
        setReservationState(convertToReservationState());
    }
    const changeFinalized = value => {
        setReservationState(value);
    }
    const togglePenalizeStudent = () => {
        setPenalizedStudent(prev => !prev);
    }
    const isValidChangeState = () => {
        const { responsable, others } = reservation.student;
        const allStudents = [
            responsable,
            ...others
        ];
        return allStudents.filter(({ attended }) => attended).length >= 2;
    }
    const penalizeDialogMustOpen = () => {
        return reservationState === RESERVATION_STATE_VALUES.FINALIZED && penalizedStudent;
    }
    const saveInformation = async () => {
        if (isGroupRoom && !isManual && !isValidChangeState()) {
            dialogShowCannotSaveInformation.open();
            return;
        }
        if (penalizeDialogMustOpen()) {
            dialogShowPenalizeDialog.open();
            return;
        }
        const reservationType = isManual 
            ? RESERVATION_TYPES.MANUAL 
            : RESERVATION_TYPES.REGULAR;
        loadingRequest.show("Guardando información...");
        const success = await requestSaveReservationState({
            reservationId: reservation.id,
            finalized: reservationState === RESERVATION_STATE_VALUES.FINALIZED,
            reservationType
        });
        loadingRequest.hide();
        if (!success) {
            mainContext.setShowDialogUnexpectedError(true);
            return;
        }
        reloadData(TOAST_MESSAGES.SAVE_RESERVATION_STATE);
    }
    const renderControls = () => {
        let styles = CONTROLS_STYLES[isManual];
        if (isGroupRoom)
            styles.wrapper.justifyContent = "center";
        return (
            <Row 
                marginTop="5px" 
                marginBottom="5px"
                justifyContent="flex-start"
                width="100%"
                {...styles.wrapper}>
                <Row {...styles.content}>
                    {RESERVATION_STATES.map(({
                        label, value
                    }, idx) => (
                        <CustomRadio
                            key={idx}
                            label={label} 
                            checked={value === reservationState}
                            onChange={() => changeFinalized(value)}
                            disabled={finalized}/>
                    ))}
                </Row>
                {(!isManual && !isGroupRoom) && 
                <CustomCheckbox 
                    label="Sancionar al alumno"
                    checked={penalizedStudent}
                    onChange={togglePenalizeStudent}
                    disabled={reservationState !== RESERVATION_STATE_VALUES.FINALIZED || responsableStudent?.penalty}
                    padding="2px"
                    size="small"/>}
            </Row>
        );
    }
    const hasBeenChanges = () => {
        return convertToReservationState() === reservationState;
    }
    const getDisableSaveInformation = () => {
        if (isManual)
            return (
                !reservationState || 
                (hasReservationState && (finalized || hasBeenChanges()))
            );
        return (
            !reservationState || 
            (hasReservationState && hasBeenChanges() && reservationState === RESERVATION_STATE_VALUES.ON_USE)
        );
    }
    const canShowPenalizeDialog = () => {
        return !isManual && showUpdateDialog && !disableSaveInformation && !isGroupRoom;
    }
    //#endregion    
    const disableSaveInformation = getDisableSaveInformation();
    return (
        <>
        <Column>
            <SubtitleSection>Estado de reserva</SubtitleSection>
            {renderControls()}
            <CustomButton 
                text="Guardar información" 
                icon={<DisketIcon/>}
                width="100%"
                onClick={() => {
                    if (penalizedStudent || isGroupRoom)
                        saveInformation();
                    else dialogShowSaveInformationConfirmation.open();
                }}
                disabled={disableSaveInformation}/>
            {canShowPenalizeDialog() && 
            <PenalizeDialog 
                dialogShow={dialogShowPenalizeDialog}
                form={penalizeForm}
                setForm={setPenalizeForm}
                penalty={{
                    data: penalizeData,
                    value: responsableStudent.penalty
                }}
                applyPenalty={applyPenalty}/>}
        </Column>
        <DialogAlertAction 
            variant="secondary"
            toggleShow={dialogShowSaveInformationConfirmation.toggle}
            show={dialogShowSaveInformationConfirmation.value}
            title={`¿Deseas guardar en uso la reserva del recurso ${resourceName}?`}
            content="Recuerda que al guardar en uso la reserva de un recurso, podrás visualizarla en la pantalla principal.">
            <CustomButton 
                variant="secondary"
                text="No, cancelar"
                width="140px"
                onClick={() => dialogShowSaveInformationConfirmation.set(false)}/>
            <CustomButton 
                text="Sí, guardar"
                width="140px"
                onClick={saveInformation}/>
        </DialogAlertAction>
        <DialogAlertAction 
            variant="secondary"
            toggleShow={dialogShowCannotSaveInformation.toggle}
            show={dialogShowCannotSaveInformation.value}
            title={`No se puede guardar el cambio de estado
            de la reserva del recurso ${resourceName}`}
            content="Recuerda que para guardar en uso la reserva de un recurso, debes registrar previamente la asistencia de los alumnos.">
            <CustomButton 
                text="Cerrar"
                width="120px"
                onClick={() => dialogShowCannotSaveInformation.set(false)}/>
        </DialogAlertAction>
        </>
    );
}

export default ReservationState;